import type { AuthCookieData } from './auth-cookie.utils'

export const AUTH_COOKIE_NAME = 'catv2'

export interface AuthCookieHeaderOptions {
  path?: string
  expires?: Date | number
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
  maxAge?: number
}

export const AUTH_COOKIE_WRITE_OPTIONS = {
  path: '/',
  secure: true,
  sameSite: 'None' as const,
  maxAge: 365 * 24 * 60 * 60,
}

export interface ServerRequestEvent {
  node: {
    req: {
      headers: {
        cookie?: string | string[]
        /** Read by the SSR auto-logout diagnostic. `node:http` collapses repeats into an array. */
        'user-agent'?: string | string[]
      }
    }
    res: {
      setHeader: (name: string, value: string | string[]) => void
    }
  }
}

/** Nitro/H3 event with auth middleware context (`libs/middleware/auth.ts` sets `context.auth`). */
export interface SsrAuthEvent extends ServerRequestEvent {
  context?: {
    auth?: AuthCookieData
  }
}

export interface SsrAuthEventOptions {
  ssrEvent?: SsrAuthEvent
}

export function isServerRequestEvent(value: unknown): value is ServerRequestEvent {
  if (!value || typeof value !== 'object') {
    return false
  }

  const node = (value as ServerRequestEvent).node
  return Boolean(node?.req && typeof node.res?.setHeader === 'function')
}

export function asSsrAuthEvent(value: unknown): SsrAuthEvent | undefined {
  return isServerRequestEvent(value) ? value : undefined
}

function getRequestCookieHeader(event: ServerRequestEvent): string | undefined {
  const cookieHeader = event.node.req.headers.cookie
  if (!cookieHeader) {
    return undefined
  }
  return Array.isArray(cookieHeader) ? cookieHeader.join('; ') : cookieHeader
}

function decodeCookieHeaderValue(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function parseRequestCookieHeader(cookieHeader: string | undefined): Record<string, string> {
  if (!cookieHeader) {
    return {}
  }

  const cookies: Record<string, string> = {}

  for (const segment of cookieHeader.split(';')) {
    const trimmed = segment.trim()
    if (!trimmed) {
      continue
    }

    const separatorIndex = trimmed.indexOf('=')
    if (separatorIndex === -1) {
      continue
    }

    const name = trimmed.slice(0, separatorIndex).trim()
    const rawValue = trimmed.slice(separatorIndex + 1)
    if (name) {
      cookies[name] = decodeCookieHeaderValue(rawValue)
    }
  }

  return cookies
}

function formatSameSiteAttribute(sameSite: AuthCookieHeaderOptions['sameSite']): string | undefined {
  if (!sameSite) {
    return undefined
  }

  return sameSite.charAt(0).toUpperCase() + sameSite.slice(1).toLowerCase()
}

function buildSetCookieHeader(name: string, value: string, options: AuthCookieHeaderOptions = {}): string {
  const parts = [`${name}=${value}`, `Path=${options.path ?? '/'}`]

  if (options.maxAge === 0) {
    parts.push('Max-Age=0', `Expires=${new Date(0).toUTCString()}`)
  } else if (options.maxAge != null && options.maxAge > 0) {
    parts.push(`Max-Age=${options.maxAge}`)
  } else if (options.expires instanceof Date) {
    parts.push(`Expires=${options.expires.toUTCString()}`)
  } else if (typeof options.expires === 'number') {
    const expiresDate = new Date()
    expiresDate.setTime(expiresDate.getTime() + options.expires * 24 * 60 * 60 * 1000)
    parts.push(`Expires=${expiresDate.toUTCString()}`)
  }

  if (options.domain) {
    parts.push(`Domain=${options.domain}`)
  }

  if (options.secure !== false) {
    parts.push('Secure')
  }

  const sameSite = formatSameSiteAttribute(options.sameSite ?? 'None')
  if (sameSite) {
    parts.push(`SameSite=${sameSite}`)
  }

  return parts.join('; ')
}

/** Read the raw encoded `catv2` value from the incoming request Cookie header. */
export function readRawAuthCookieFromEvent(event: ServerRequestEvent): string | null {
  const cookies = parseRequestCookieHeader(getRequestCookieHeader(event))
  return cookies[AUTH_COOKIE_NAME] ?? null
}

/** Write the encoded `catv2` value on the SSR response. */
export function writeAuthCookieOnEvent(
  event: ServerRequestEvent,
  encodedValue: string,
  options: AuthCookieHeaderOptions = AUTH_COOKIE_WRITE_OPTIONS
): void {
  event.node.res.setHeader('Set-Cookie', buildSetCookieHeader(AUTH_COOKIE_NAME, encodedValue, options))
}

/** Expire `catv2` on the SSR response (used when the cookie is malformed). */
export function clearAuthCookieOnEvent(event: ServerRequestEvent, options: AuthCookieHeaderOptions = {}): void {
  writeAuthCookieOnEvent(event, '', {
    ...AUTH_COOKIE_WRITE_OPTIONS,
    ...options,
    maxAge: 0,
  })
}
